import React, { useState, useEffect, useRef, useMemo } from 'react';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { requirementService } from '../../services/requirementService';
import { supplierService } from '../../services/supplierService';
import SupplierSelector from './SupplierSelector';
import ProductSelector from './ProductSelector';
import UnitSelect from '../Common/UnitSelect';
import QuantityConversionHint from '../Common/QuantityConversionHint';
import PriceIgvHint from '../Common/PriceIgvHint';
import PricesIncludeIgvToggle from '../Common/PricesIncludeIgvToggle';
import SubtotalQuantityModeToggle from '../Common/SubtotalQuantityModeToggle';
import LineItemField from '../Common/LineItemField';
import UnitPriceTextInput from '../Common/UnitPriceTextInput';
import QuantityTextInput from '../Common/QuantityTextInput';
import { adjustNetPriceOnHasIgvChange, isItemTaxable, roundPrice } from '../../utils/priceIgvUtils';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { useComprasPurchaseSiteId } from '../../utils/comprasPurchaseSite';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import CreateSupplierModal from './CreateSupplierModal';
import FormLoadingSpinner from '../Common/FormLoadingSpinner';
import Toast from '../Common/Toast';
import ComprasItemsImportPreviewModal from '../Common/ComprasItemsImportPreviewModal';
import RequirementSelectorModal from '../Requirements/RequirementSelectorModal';
import {
  downloadPurchaseOrderItemsTemplate,
  parsePurchaseOrderItemsExcel,
} from '../../utils/comprasItemsExcelImport';
import { getItemLineSubtotal, itemHasCustomConversion } from '../../utils/unitSelectUtils';
import QuickProductLineBar from '../Common/QuickProductLineBar';
import ProductLineDefaultsBar from '../Common/ProductLineDefaultsBar';
import { emptyPurchaseOrderStagingItem } from '../../utils/quickProductLineFactories';
import {
  applyProductLineDefaults,
  buildProductLineSettings,
  emptyStagingWithDefaults,
  mergeProductLineDefaults,
} from '../../utils/productLineDefaults';
import { handlePreventEnterFormSubmit } from '../../utils/preventEnterFormSubmit';
import { PO_ITEM_ROW, POIC } from '../../utils/purchaseOrderItemRowLayout';

const stripImportMeta = ({ _label, _unitFormula, _unitIsCustom, ...item }) => item;

const buildLinkedRequirementsFromOrder = (orderData) => {
  const byId = new Map();
  (orderData?.requirements || []).forEach((r) => {
    if (r?.id) {
      byId.set(r.id, { id: r.id, code: r.code || `REQ #${r.id}` });
    }
  });
  (orderData?.items || []).forEach((item) => {
    const reqId = item.requirement_item?.requirement_id;
    if (reqId && !byId.has(reqId)) {
      byId.set(reqId, { id: reqId, code: `REQ #${reqId}` });
    }
  });
  return [...byId.values()];
};

const PurchaseOrderForm = ({ isOpen, onClose, order = null, onSuccess, isPageMode = false, readOnly = false }) => {
  const { productConversionsEnabled, quickProductLineEnabled, productsProjectEnabled, productsIgvEnabled } = useModuleSettings();
  const purchaseSiteId = useComprasPurchaseSiteId();
  const [defaultLineProjectId, setDefaultLineProjectId] = useState('');
  const [defaultLineHasIgv, setDefaultLineHasIgv] = useState(true);
  const productLineSettings = useMemo(
    () => buildProductLineSettings({
      productsProjectEnabled,
      productsIgvEnabled,
      defaultProjectId: defaultLineProjectId,
      defaultHasIgv: defaultLineHasIgv,
    }),
    [productsProjectEnabled, productsIgvEnabled, defaultLineProjectId, defaultLineHasIgv],
  );
  const showProductLineDefaults = productsProjectEnabled || productsIgvEnabled;
  const [formData, setFormData] = useState({
    supplier_id: '',
    igv_rate: '18',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    payment_condition_type: 'cash', // 'cash', 'days' o 'date'
    payment_condition_days: 30, // Días para pagar (si type es 'days')
    payment_condition_date: '', // Fecha específica (si type es 'date')
    notes: '',
    items: [], // Array de { product_id, quantity, unit_price, has_igv }
  });

  const [suppliers, setSuppliers] = useState([]);
  const {
    products,
    getProduct: getCatalogProduct,
    mergeProducts,
    ensureByIds,
    searchProducts,
  } = useProductCatalog(null);
  const findProductById = (id, fallback = null) => getCatalogProduct(id, fallback);
  const productSelectorAsyncProps = {
    asyncSearch: true,
    knownProducts: products,
    onSearchProducts: searchProducts,
    onEnsureProduct: (id) => ensureByIds([id]),
  };
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formDataLoading, setFormDataLoading] = useState(true);
  const [orderHydrated, setOrderHydrated] = useState(() => !(order && order.id));
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [codePreview, setCodePreview] = useState('');
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const [isEditingAmounts, setIsEditingAmounts] = useState(false);
  const [amountOverride, setAmountOverride] = useState(null);
  const [amountForm, setAmountForm] = useState({ igv_amount: '' });
  const hasLoadedFormData = useRef(false);
  const hasLoadedOrder = useRef(false);
  const lastOrderIdRef = useRef(null);
  const itemsImportFileRef = useRef(null);
  const [importingItems, setImportingItems] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [pendingRequirements, setPendingRequirements] = useState([]);
  const [linkedRequirements, setLinkedRequirements] = useState([]);
  const [isRequirementSelectorModalOpen, setIsRequirementSelectorModalOpen] = useState(false);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [pricesIncludeIgv, setPricesIncludeIgv] = useState(false);
  const [useConvertedQuantityForSubtotal, setUseConvertedQuantityForSubtotal] = useState(true);
  const [stagingItem, setStagingItem] = useState(() => emptyPurchaseOrderStagingItem());
  const [stagingError, setStagingError] = useState('');
  const stagingProductSelectorRef = useRef(null);

  useEffect(() => {
    if (!showProductLineDefaults) return;
    setStagingItem((prev) => mergeProductLineDefaults(prev, productLineSettings));
  }, [productLineSettings, showProductLineDefaults]);

  // Cargar datos del formulario (una sola petición API)
  useEffect(() => {
    if ((isOpen || isPageMode) && !hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, [isOpen, isPageMode]);

  useEffect(() => {
    const ids = (formData.items || []).map((item) => item.product_id).filter(Boolean);
    if (ids.length > 0) ensureByIds(ids).catch(() => {});
  }, [formData.items, ensureByIds]);

  // Al cerrar el modal, resetear para que al reabrir se recarguen datos
  useEffect(() => {
    if (!isOpen && !isPageMode) {
      hasLoadedFormData.current = false;
    }
  }, [isOpen, isPageMode]);

  // Generar preview de código
  useEffect(() => {
    if (!order) {
      setCodePreview('OC-XXXX');
    }
  }, [order]);

  // Cargar orden si se pasa un ID (modo edición)
  useEffect(() => {
    if (order && typeof order === 'object' && order.id) {
      if (lastOrderIdRef.current !== order.id) {
        lastOrderIdRef.current = order.id;
        hasLoadedOrder.current = false;
        setOrderHydrated(false);
      }
      if (!hasLoadedOrder.current) {
        console.log('📝 [PurchaseOrderForm] Cargando datos de orden para edición:', order);
        loadOrderDataFromObject(order);
      }
    } else {
      lastOrderIdRef.current = null;
      setOrderHydrated(true);
    }
  }, [order]);

  const loadFormData = async () => {
    setFormDataLoading(true);
    try {
      const res = await purchaseOrderService.getFormData();
      const d = res.data || {};
      if (res.success) {
        setSuppliers(d.suppliers || []);
        setUnits(d.units || []);
        setProjects(d.projects || []);
      }
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los datos del formulario',
        type: 'error'
      });
    } finally {
      setFormDataLoading(false);
    }
  };

  const handleSupplierCreated = async (newSupplier) => {
    // Recargar la lista completa de proveedores desde el backend
    try {
      const response = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
      if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error al recargar proveedores:', error);
      // Si falla, al menos agregar el nuevo proveedor localmente
      setSuppliers(prev => [...prev, newSupplier]);
    }
    
    // Seleccionar el nuevo proveedor
    setFormData(prev => ({
      ...prev,
      supplier_id: newSupplier.id.toString(),
    }));
    setToast({
      isOpen: true,
      message: 'Proveedor creado y seleccionado exitosamente',
      type: 'success'
    });
  };

  // Función auxiliar para convertir fecha ISO a formato YYYY-MM-DD
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return '';
    }
  };

  const loadOrderDataFromObject = (orderData) => {
    hasLoadedOrder.current = true;
    
    const formattedOrderDate = formatDateForInput(orderData.order_date);
    const formattedDeliveryDate = formatDateForInput(orderData.expected_delivery_date);
    const formattedPaymentDate = formatDateForInput(orderData.payment_condition_date);
    
    console.log('📅 [PurchaseOrderForm] Formateando fechas:', {
      original_order_date: orderData.order_date,
      formatted_order_date: formattedOrderDate,
      original_expected_delivery_date: orderData.expected_delivery_date,
      formatted_expected_delivery_date: formattedDeliveryDate,
      original_payment_condition_date: orderData.payment_condition_date,
      formatted_payment_condition_date: formattedPaymentDate,
    });
    
    setFormData({
      supplier_id: orderData.supplier_id ? String(orderData.supplier_id) : '',
      igv_rate: String(orderData.items?.[0]?.igv_rate ?? 18),
      order_date: formattedOrderDate || new Date().toISOString().split('T')[0],
      expected_delivery_date: formattedDeliveryDate || '',
      payment_condition_type: orderData.payment_condition_type || 'cash',
      payment_condition_days: orderData.payment_condition_days || 30,
      payment_condition_date: formattedPaymentDate || '',
      notes: orderData.notes || '',
      items: orderData.items?.map(item => ({
        id: item.id,
        product_id: item.product_id,
        unit_of_measure: item.unit_of_measure || item.product?.unit_of_measure || '',
        project_id: item.project_id || item.project?.id || '',
        quantity: item.quantity,
        unit_price: item.unit_price,
        has_igv: item.has_igv ?? true,
        requirement_item_id: item.requirement_item_id || null,
      })) || [],
    });

    setLinkedRequirements(buildLinkedRequirementsFromOrder(orderData));
    mergeProducts((orderData.items || []).map((item) => item.product).filter(Boolean));
    setUseConvertedQuantityForSubtotal(orderData.use_converted_quantity_for_subtotal !== false);

    if (orderData.code) {
      setCodePreview(orderData.code);
    }

    setOrderHydrated(true);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.supplier_id) {
      newErrors.supplier_id = 'El proveedor es requerido';
    }

    if (!formData.order_date) {
      newErrors.order_date = 'La fecha de orden es requerida';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Debe agregar al menos un item a la orden';
    }

    formData.items.forEach((item, index) => {
      if (!item.product_id) {
        newErrors[`items.${index}.product_id`] = 'El producto es requerido';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`items.${index}.quantity`] = 'La cantidad debe ser mayor a 0';
      }
      if (!item.unit_price || item.unit_price <= 0) {
        newErrors[`items.${index}.unit_price`] = 'El precio unitario debe ser mayor a 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        mergeProductLineDefaults(
          { product_id: '', unit_of_measure: '', project_id: '', quantity: 1, unit_price: 0, has_igv: true },
          productLineSettings,
        ),
      ],
    }));
  };

  const handleStagingChange = (field, value) => {
    setStagingError('');
    setStagingItem((prev) => {
      let updated = { ...prev };
      if (field === 'quantity') {
        updated.quantity = parseFloat(value) || 0;
      } else if (field === 'unit_price') {
        updated.unit_price = roundPrice(parseFloat(value) || 0);
      } else if (field === 'has_igv') {
        const newHasIgv = value === 'true' || value === true;
        const oldHasIgv = prev.has_igv ?? true;
        updated.has_igv = newHasIgv;
        if (oldHasIgv !== newHasIgv) {
          updated.unit_price = adjustNetPriceOnHasIgvChange(prev.unit_price, {
            oldHasIgv,
            newHasIgv,
            pricesIncludeIgv,
            igvRate: formData.igv_rate,
          });
        }
      } else {
        updated[field] = value;
      }
      if (field === 'product_id' && value) {
        const product = findProductById(value);
        if (product?.unit_of_measure) {
          updated.unit_of_measure = product.unit_of_measure;
        } else {
          updated.unit_of_measure = '';
        }
        const defaults = applyProductLineDefaults(productLineSettings);
        if (defaults.project_id !== undefined) {
          updated.project_id = defaults.project_id;
        }
        if (defaults.has_igv !== undefined) {
          const oldHasIgv = prev.has_igv ?? true;
          updated.has_igv = defaults.has_igv;
          if (oldHasIgv !== defaults.has_igv) {
            updated.unit_price = adjustNetPriceOnHasIgvChange(prev.unit_price, {
              oldHasIgv,
              newHasIgv: defaults.has_igv,
              pricesIncludeIgv,
              igvRate: formData.igv_rate,
            });
          }
        }
      }
      return updated;
    });
  };

  const handleQuickAddItem = () => {
    if (!stagingItem.product_id) {
      setStagingError('Seleccione un producto');
      return;
    }
    if (!stagingItem.quantity || stagingItem.quantity <= 0) {
      setStagingError('La cantidad debe ser mayor a 0');
      return;
    }
    if (!stagingItem.unit_price || stagingItem.unit_price <= 0) {
      setStagingError('El precio unitario debe ser mayor a 0');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...stagingItem }],
    }));
    setStagingItem(emptyStagingWithDefaults(emptyPurchaseOrderStagingItem, productLineSettings));
    setStagingError('');
    window.setTimeout(() => stagingProductSelectorRef.current?.focus?.(), 0);
  };

  const handleDownloadItemsTemplate = () => {
    downloadPurchaseOrderItemsTemplate();
  };

  const handlePickItemsImport = () => {
    itemsImportFileRef.current?.click();
  };

  const handleItemsImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const nameOk = /\.xlsx$/i.test(file.name || '') || (file.type || '').includes('spreadsheetml');
    if (!nameOk) {
      setToast({
        isOpen: true,
        message: 'Use un archivo Excel .xlsx (misma estructura que la plantilla descargada).',
        type: 'error',
      });
      return;
    }
    if (!products.length) {
      setToast({
        isOpen: true,
        message: 'Espere a que carguen los productos del formulario.',
        type: 'warning',
      });
      return;
    }
    setImportingItems(true);
    try {
      const { items: parsed, errors, warnings } = await parsePurchaseOrderItemsExcel(file, {
        products,
        units,
        projects,
        productConversionsEnabled,
      });
      if (parsed.length === 0 && !errors.length) {
        setToast({ isOpen: true, message: 'No se importó ninguna fila con datos.', type: 'warning' });
        return;
      }
      setImportPreview({
        fileName: file.name,
        items: parsed,
        errors: errors || [],
        warnings: warnings || [],
      });
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al leer el archivo Excel.',
        type: 'error',
      });
    } finally {
      setImportingItems(false);
    }
  };

  const handleConfirmImportPreview = (confirmedRows) => {
    const source = confirmedRows || importPreview?.items || [];
    if (!source.length) {
      setImportPreview(null);
      return;
    }
    const toAdd = source.map(stripImportMeta).map((item) => ({
      ...item,
      product_id: String(item.product_id),
      project_id: item.project_id ? String(item.project_id) : '',
    }));
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, ...toAdd],
    }));
    setImportPreview(null);
    const wMsg = importPreview.warnings?.length
      ? ` Advertencias: ${importPreview.warnings.slice(0, 3).join(' ')}${importPreview.warnings.length > 3 ? '…' : ''}`
      : '';
    setToast({
      isOpen: true,
      message: `Se añadieron ${toAdd.length} fila(s) a la orden.${wMsg}`,
      type: 'info',
    });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      let updatedItem = { ...currentItem };

      if (field === 'quantity') {
        updatedItem.quantity = parseFloat(value) || 0;
      } else if (field === 'unit_price') {
        updatedItem.unit_price = roundPrice(parseFloat(value) || 0);
      } else if (field === 'has_igv') {
        const newHasIgv = value === 'true' || value === true;
        const oldHasIgv = currentItem.has_igv ?? true;
        updatedItem.has_igv = newHasIgv;
        if (oldHasIgv !== newHasIgv) {
          updatedItem.unit_price = adjustNetPriceOnHasIgvChange(currentItem.unit_price, {
            oldHasIgv,
            newHasIgv,
            pricesIncludeIgv,
            igvRate: prev.igv_rate,
          });
        }
      } else {
        updatedItem[field] = value;
      }

      // Si se cambia el producto, actualizar automáticamente la unidad de medida del producto
      // como valor por defecto (opcional, el usuario puede cambiarla)
      if (field === 'product_id' && value) {
        const product = findProductById(value);
        console.log('🔄 [PurchaseOrderForm] Producto seleccionado:', product);
        console.log('📏 [PurchaseOrderForm] Unidad de medida del producto:', product?.unit_of_measure);
        if (product && product.unit_of_measure) {
          updatedItem.unit_of_measure = product.unit_of_measure;
          console.log('✅ [PurchaseOrderForm] Unidad actualizada a:', product.unit_of_measure);
        } else {
          updatedItem.unit_of_measure = '';
          console.log('⚠️ [PurchaseOrderForm] Producto sin unidad de medida');
        }
        const defaults = applyProductLineDefaults(productLineSettings);
        if (defaults.project_id !== undefined) {
          updatedItem.project_id = defaults.project_id;
        }
        if (defaults.has_igv !== undefined) {
          const oldHasIgv = currentItem.has_igv ?? true;
          updatedItem.has_igv = defaults.has_igv;
          if (oldHasIgv !== defaults.has_igv) {
            updatedItem.unit_price = adjustNetPriceOnHasIgvChange(currentItem.unit_price, {
              oldHasIgv,
              newHasIgv: defaults.has_igv,
              pricesIncludeIgv,
              igvRate: prev.igv_rate,
            });
          }
        }
      }

      newItems[index] = updatedItem;
      return {
        ...prev,
        items: newItems,
      };
    });
  };

  const calculateTotal = () => {
    const igvRatePercent = parseFloat(formData.igv_rate) || 18;
    const IGV_RATE = igvRatePercent / 100;
    
    let baseImponible = 0; // Items con IGV
    let noGravados = 0; // Items sin IGV
    
    formData.items.forEach(item => {
      const product = findProductById(item.product_id, item.product);
      const subtotal = getItemLineSubtotal({
        product,
        unitCode: item.unit_of_measure,
        quantity: item.quantity,
        unitPrice: item.unit_price,
        productConversionsEnabled,
        useConvertedQuantityForSubtotal,
      });
      if (isItemTaxable(item.has_igv ?? true)) {
        baseImponible += subtotal;
      } else {
        noGravados += subtotal;
      }
    });
    
    const igv = baseImponible * IGV_RATE;
    const total = baseImponible + igv + noGravados;
    
    return {
      baseImponible,
      igv,
      noGravados,
      total
    };
  };

  // Calcular fecha de pago basándose en la condición seleccionada
  const calculatePaymentDate = () => {
    if (!formData.order_date) return null;
    
    const orderDate = new Date(formData.order_date);
    
    if (formData.payment_condition_type === 'cash') {
      // Al contado: la fecha de pago es la misma que la fecha de orden
      return orderDate;
    } else if (formData.payment_condition_type === 'days') {
      const days = parseInt(formData.payment_condition_days) || 0;
      const paymentDate = new Date(orderDate);
      paymentDate.setDate(paymentDate.getDate() + days);
      return paymentDate;
    } else if (formData.payment_condition_type === 'date' && formData.payment_condition_date) {
      return new Date(formData.payment_condition_date);
    }
    
    return null;
  };

  // Calcular días hasta la fecha de pago
  const calculateDaysUntilPayment = () => {
    const paymentDate = calculatePaymentDate();
    if (!paymentDate || !formData.order_date) return null;
    
    const orderDate = new Date(formData.order_date);
    const diffTime = paymentDate - orderDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const loadPendingRequirements = async () => {
    setLoadingRequirements(true);
    try {
      const reqRes = await requirementService.getPendingRequirements();
      if (reqRes.success) setPendingRequirements(reqRes.data || []);
    } catch {
      setToast({
        isOpen: true,
        message: 'No se pudieron cargar los requerimientos pendientes',
        type: 'error',
      });
    } finally {
      setLoadingRequirements(false);
    }
  };

  useEffect(() => {
    if (isRequirementSelectorModalOpen) {
      loadPendingRequirements();
    }
  }, [isRequirementSelectorModalOpen]);

  const importFromRequirements = (requirementIds) => {
    const ids = Array.isArray(requirementIds) ? requirementIds : [];
    if (!ids.length) return;

    const newLines = [];
    const codes = [];
    const linked = new Set();

    ids.forEach((reqId) => {
      const req = pendingRequirements.find((r) => String(r.id) === String(reqId));
      if (!req?.items?.length) return;
      linked.add(req.id);
      codes.push(req.code);
      req.items.forEach((ri) => {
        const pending = parseFloat(ri.pending_quantity ?? ri.quantity ?? 0);
        if (pending <= 0) return;
        const exists = newLines.some((l) => l.requirement_item_id === ri.id);
        if (exists) return;
        newLines.push({
          product_id: String(ri.product_id),
          quantity: String(pending),
          unit_price: '0',
          unit_of_measure: ri.unit_of_measure || '',
          project_id: ri.project_id ? String(ri.project_id) : '',
          has_igv: true,
          requirement_item_id: ri.id,
        });
      });
    });

    if (!newLines.length) {
      setToast({
        isOpen: true,
        message: 'Los requerimientos seleccionados no tienen cantidades pendientes',
        type: 'info',
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items.filter((i) => i.product_id), ...newLines],
    }));
    setLinkedRequirements((prev) => {
      const map = new Map(prev.map((r) => [r.id, r]));
      linked.forEach((reqId) => {
        const req = pendingRequirements.find((r) => String(r.id) === String(reqId));
        if (req) {
          map.set(req.id, { id: req.id, code: req.code || `REQ #${req.id}` });
        }
      });
      return [...map.values()];
    });
    setIsRequirementSelectorModalOpen(false);
    setToast({
      isOpen: true,
      message: `Ítems cargados desde ${codes.join(', ')}. Complete precios del proveedor.`,
      type: 'success',
    });
  };

  const linkedRequirementIds = linkedRequirements.map((r) => r.id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    if (!validate()) {
      setToast({
        isOpen: true,
        message: 'Por favor, corrige los errores en el formulario',
        type: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const paymentDate = calculatePaymentDate();
      const calculated = calculateTotal();
      const totals = amountOverride || calculated;

      const submitData = {
        supplier_id: parseInt(formData.supplier_id),
        igv_rate: parseFloat(formData.igv_rate),
        order_date: formData.order_date,
        expected_delivery_date: formData.expected_delivery_date || null,
        payment_condition_type: formData.payment_condition_type,
        payment_condition_days: formData.payment_condition_type === 'days' ? parseInt(formData.payment_condition_days) : null,
        payment_condition_date: formData.payment_condition_type === 'date' ? formData.payment_condition_date : null,
        payment_date: paymentDate ? paymentDate.toISOString().split('T')[0] : null,
        notes: formData.notes,
        base_imponible: totals.baseImponible,
        igv_amount: totals.igv,
        no_gravados: totals.noGravados,
        total_amount: totals.total,
        use_converted_quantity_for_subtotal: useConvertedQuantityForSubtotal,
        requirement_ids: linkedRequirementIds.map((id) => parseInt(id, 10)),
        items: formData.items.map(item => {
          const product = findProductById(item.product_id, item.product);
          const lineSubtotal = getItemLineSubtotal({
            product,
            unitCode: item.unit_of_measure,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            productConversionsEnabled,
            useConvertedQuantityForSubtotal,
          });
          return {
            product_id: parseInt(item.product_id),
            unit_of_measure: item.unit_of_measure || null,
            project_id: item.project_id ? parseInt(item.project_id) : null,
            requirement_item_id: item.requirement_item_id ? parseInt(item.requirement_item_id, 10) : null,
            quantity: parseFloat(item.quantity),
            unit_price: parseFloat(item.unit_price),
            subtotal: Math.round(lineSubtotal * 100) / 100,
            has_igv: item.has_igv ?? true,
          };
        }),
        ...(purchaseSiteId && !order?.id ? { purchase_site_id: purchaseSiteId } : {}),
      };

      let response;
      if (order && order.id) {
        response = await purchaseOrderService.updatePurchaseOrder(order.id, submitData);
        setToast({
          isOpen: true,
          message: 'Orden de compra actualizada exitosamente',
          type: 'success'
        });
      } else {
        response = await purchaseOrderService.createPurchaseOrder(submitData);
        setToast({
          isOpen: true,
          message: 'Orden de compra creada exitosamente',
          type: 'success'
        });
      }

      if (response.success) {
        if (response.data) {
          setLinkedRequirements(buildLinkedRequirementsFromOrder(response.data));
        }
        setTimeout(() => {
          onSuccess();
          if (!isPageMode) {
            onClose();
          }
        }, 1000);
      } else {
        throw new Error(response.message || 'Error al guardar la orden de compra');
      }
    } catch (error) {
      console.error('Error:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setToast({
          isOpen: true,
          message: 'Por favor, corrige los errores en el formulario',
          type: 'error'
        });
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Error al guardar la orden de compra';
        setErrors({ submit: errorMessage });
        setToast({
          isOpen: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isPageMode) return null;

  const isInitialLoading = formDataLoading || (Boolean(order?.id) && !orderHydrated);

  const handleFormKeyDown = (e) => {
    handlePreventEnterFormSubmit(e, {
      onEnterInZone:
        !readOnly && quickProductLineEnabled ? () => handleQuickAddItem() : undefined,
    });
  };

  const formContent = (
    <>
      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className={isPageMode ? "p-8" : "px-6 py-4"}>
        {errors.submit && (
          <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {errors.submit}
          </div>
        )}

        {readOnly && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            Esta orden está completada o cerrada. Solo puede consultar la información; no es posible modificarla.
          </div>
        )}

        <fieldset disabled={readOnly} className="space-y-6 min-w-0 border-0 p-0 m-0 disabled:opacity-100">
          {/* Sección: Código Preview - Solo mostrar en modo creación */}
          {!order && codePreview && (
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-5 shadow-sm">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                Código (Generado automáticamente)
              </label>
              <div className="text-2xl font-mono font-bold text-teal-700 mb-1">{codePreview}</div>
              <p className="text-xs text-gray-600 mt-2">Este código se generará automáticamente al guardar la orden</p>
            </div>
          )}

          {/* Sección: Información Básica y Condición de Pago - Lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sección: Información Básica */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Información Básica
              </h3>
              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="supplier_id" className="block text-sm font-medium text-gray-700">
                      Proveedor <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsCreateSupplierModalOpen(true)}
                      className="text-xs px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                    >
                      + Nuevo
                    </button>
                  </div>
                  <SupplierSelector
                    value={formData.supplier_id}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        supplier_id: value.toString(),
                      }));
                      if (errors.supplier_id) {
                        setErrors(prev => ({
                          ...prev,
                          supplier_id: null,
                        }));
                      }
                    }}
                    error={errors.supplier_id}
                    suppliers={suppliers}
                  />
                </div>

                <div>
                  <label htmlFor="igv_rate" className="block text-sm font-medium text-gray-700 mb-2">
                    Tasa de IGV
                  </label>
                  <select
                    id="igv_rate"
                    name="igv_rate"
                    value={formData.igv_rate}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  >
                    <option value="18">18%</option>
                    <option value="10">10%</option>
                    <option value="10.5">10.5%</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="order_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Orden <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="order_date"
                    name="order_date"
                    value={formData.order_date}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.order_date ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                    }`}
                  />
                  {errors.order_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.order_date}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="expected_delivery_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Esperada de Entrega
                  </label>
                  <input
                    type="date"
                    id="expected_delivery_date"
                    name="expected_delivery_date"
                    value={formData.expected_delivery_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Sección: Condición de Pago */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Condición de Pago
            </h3>
            <div className="space-y-4">
              {/* Tipo de condición */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tipo de Condición
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="cash"
                      checked={formData.payment_condition_type === 'cash'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Al contado</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="days"
                      checked={formData.payment_condition_type === 'days'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Cada X días (máx. 3 meses)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="date"
                      checked={formData.payment_condition_type === 'date'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Fecha específica</span>
                  </label>
                </div>
              </div>

              {/* Opción: Cada X días */}
              {formData.payment_condition_type === 'days' && (
                <div>
                  <label htmlFor="payment_condition_days" className="block text-sm font-medium text-gray-700 mb-2">
                    Días para pagar
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      id="payment_condition_days"
                      name="payment_condition_days"
                      value={formData.payment_condition_days}
                      onChange={(e) => {
                        const days = parseInt(e.target.value) || 0;
                        const maxDays = 90; // 3 meses máximo
                        const validDays = Math.min(Math.max(1, days), maxDays);
                        setFormData(prev => ({
                          ...prev,
                          payment_condition_days: validDays,
                        }));
                      }}
                      min="1"
                      max="90"
                      className="w-32 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    />
                    <span className="text-sm text-gray-600">días (máximo 90 días / 3 meses)</span>
                  </div>
                </div>
              )}

              {/* Opción: Fecha específica */}
              {formData.payment_condition_type === 'date' && (
                <div>
                  <label htmlFor="payment_condition_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Pago
                  </label>
                  <input
                    type="date"
                    id="payment_condition_date"
                    name="payment_condition_date"
                    value={formData.payment_condition_date}
                    onChange={handleChange}
                    min={formData.order_date}
                    className="w-full md:w-64 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                </div>
              )}

              {/* Información de fecha de pago y contador de días */}
              {calculatePaymentDate() && (
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Fecha de Pago Calculada</div>
                      <div className="text-lg font-semibold text-teal-700">
                        {calculatePaymentDate().toLocaleDateString('es-PE', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        {formData.payment_condition_type === 'cash' && (
                          <span className="ml-2 text-sm text-teal-600 font-normal">(Al contado)</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Días hasta el pago</div>
                      <div className="text-2xl font-bold text-teal-600">
                        {calculateDaysUntilPayment() !== null ? (
                          <>
                            {calculateDaysUntilPayment()} 
                            <span className="text-base ml-1">
                              {calculateDaysUntilPayment() === 0 ? 'día (al contado)' : calculateDaysUntilPayment() === 1 ? 'día' : 'días'}
                            </span>
                          </>
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Sección: Items de la Orden */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex flex-col gap-3 mb-5 pb-3 border-b border-gray-200">
              <div className="flex flex-wrap justify-between items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Items de la Orden
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {!quickProductLineEnabled && (
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                  >
                    + Agregar Item
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDownloadItemsTemplate}
                  className="px-3 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Descargar plantilla
                </button>
                <input
                  ref={itemsImportFileRef}
                  type="file"
                  accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={handleItemsImportFile}
                />
                <button
                  type="button"
                  onClick={handlePickItemsImport}
                  disabled={importingItems}
                  className="px-3 py-2 border border-teal-300 bg-teal-50 text-teal-900 rounded-lg hover:bg-teal-100 text-sm font-medium disabled:opacity-50"
                >
                  {importingItems ? 'Importando…' : 'Importar Excel'}
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => setIsRequirementSelectorModalOpen(true)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    Anidar requerimiento
                  </button>
                )}
              </div>
              </div>
              {!readOnly && (
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  <PricesIncludeIgvToggle
                    value={pricesIncludeIgv}
                    onChange={setPricesIncludeIgv}
                    igvRate={formData.igv_rate}
                  />
                  {productConversionsEnabled &&
                    formData.items.some((item) =>
                      itemHasCustomConversion({
                        product: findProductById(item.product_id, item.product),
                        unitCode: item.unit_of_measure,
                        productConversionsEnabled,
                      }),
                    ) && (
                      <SubtotalQuantityModeToggle
                        value={useConvertedQuantityForSubtotal}
                        onChange={setUseConvertedQuantityForSubtotal}
                      />
                    )}
                </div>
              )}
            </div>

            {errors.items && (
              <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                {errors.items}
              </div>
            )}

            {!readOnly && quickProductLineEnabled && (
              <QuickProductLineBar
                onAdd={handleQuickAddItem}
                error={stagingError}
                rowClassName={PO_ITEM_ROW}
                defaultsSlot={
                  showProductLineDefaults ? (
                    <ProductLineDefaultsBar
                      productsProjectEnabled={productsProjectEnabled}
                      productsIgvEnabled={productsIgvEnabled}
                      projects={projects}
                      defaultProjectId={defaultLineProjectId}
                      onDefaultProjectIdChange={setDefaultLineProjectId}
                      defaultHasIgv={defaultLineHasIgv}
                      onDefaultHasIgvChange={setDefaultLineHasIgv}
                      className="mb-0"
                    />
                  ) : null
                }
              >
                <LineItemField
                  label="Producto"
                  required
                  className={POIC.product}
                >
                  <ProductSelector
                    ref={stagingProductSelectorRef}
                    value={stagingItem.product_id}
                    onChange={(value) => handleStagingChange('product_id', value)}
                    {...productSelectorAsyncProps}
                    compact
                  />
                </LineItemField>
                <LineItemField label="Unidad" className={POIC.unit}>
                  <UnitSelect
                    value={stagingItem.unit_of_measure || ''}
                    onChange={(v) => handleStagingChange('unit_of_measure', v)}
                    units={units}
                    product={findProductById(stagingItem.product_id, stagingItem.product)}
                    compact
                    emptyLabel="Seleccionar"
                    showCodeOnly={false}
                  />
                </LineItemField>
                <LineItemField label="Proyecto" className={POIC.project}>
                  <select
                    value={stagingItem.project_id || ''}
                    onChange={(e) => handleStagingChange('project_id', e.target.value)}
                    className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  >
                    <option value="">Seleccionar</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.code} - {project.name}
                      </option>
                    ))}
                  </select>
                </LineItemField>
                <LineItemField label="Cant." required className={POIC.qty}>
                  <QuantityTextInput
                    value={stagingItem.quantity}
                    onChange={(qty) => handleStagingChange('quantity', qty)}
                    min={0.001}
                    className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    placeholder="0"
                  />
                </LineItemField>
                <LineItemField label="Precio Unit." required className={POIC.price}>
                  <div className="relative w-full">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                    <UnitPriceTextInput
                      netPrice={stagingItem.unit_price}
                      onNetPriceChange={(net) => handleStagingChange('unit_price', net)}
                      pricesIncludeIgv={pricesIncludeIgv}
                      hasIgv={stagingItem.has_igv ?? true}
                      igvRate={formData.igv_rate}
                      className="w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                      placeholder="0.0000"
                    />
                  </div>
                </LineItemField>
                <LineItemField label="IGV" className={POIC.igv}>
                  <select
                    value={(stagingItem.has_igv ?? true) ? 'true' : 'false'}
                    onChange={(e) => handleStagingChange('has_igv', e.target.value)}
                    className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  >
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </LineItemField>
              </QuickProductLineBar>
            )}

            {linkedRequirementIds.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Requerimientos vinculados:</span>
                {linkedRequirements.map((req) => (
                  <span
                    key={req.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono bg-teal-100 text-teal-800"
                  >
                    {req.code}
                  </span>
                ))}
              </div>
            )}

            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No hay items agregados</p>
                <p className="text-sm mt-2">Agregue ítems manualmente o importe desde Excel con la plantilla</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.items.map((item, index) => {
                  const product = findProductById(item.product_id, item.product);
                  const subtotal = getItemLineSubtotal({
                    product,
                    unitCode: item.unit_of_measure,
                    quantity: item.quantity,
                    unitPrice: item.unit_price,
                    productConversionsEnabled,
                    useConvertedQuantityForSubtotal,
                  });
                  
                  return (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className={PO_ITEM_ROW}>
                        <LineItemField
                          label="Producto"
                          required
                          className={POIC.product}
                        >
                          <ProductSelector
                            value={item.product_id}
                            onChange={(value) => handleItemChange(index, 'product_id', value)}
                            {...productSelectorAsyncProps}
                            error={errors[`items.${index}.product_id`]}
                            compact={true}
                          />
                        </LineItemField>

                        <LineItemField label="Unidad" className={POIC.unit}>
                          <UnitSelect
                            value={item.unit_of_measure || ''}
                            onChange={(v) => handleItemChange(index, 'unit_of_measure', v)}
                            units={units}
                            product={findProductById(item.product_id, item.product)}
                            compact
                            emptyLabel="Seleccionar"
                            showCodeOnly={false}
                          />
                        </LineItemField>

                        <LineItemField label="Proyecto" className={POIC.project}>
                          <select
                            value={item.project_id || ''}
                            onChange={(e) => handleItemChange(index, 'project_id', e.target.value)}
                            className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          >
                            <option value="">Seleccionar</option>
                            {projects.map((project) => (
                              <option key={project.id} value={project.id}>
                                {project.code} - {project.name}
                              </option>
                            ))}
                          </select>
                        </LineItemField>

                        <LineItemField
                          label="Cant."
                          required
                          className={POIC.qty}
                          hint={
                            <QuantityConversionHint
                              product={findProductById(item.product_id, item.product)}
                              unitCode={item.unit_of_measure}
                              quantity={item.quantity}
                            />
                          }
                        >
                          <QuantityTextInput
                            value={item.quantity}
                            onChange={(qty) => handleItemChange(index, 'quantity', qty)}
                            min={0.001}
                            className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                              errors[`items.${index}.quantity`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                            }`}
                            placeholder="0"
                          />
                        </LineItemField>

                        <LineItemField
                          label="Precio Unit."
                          required
                          className={POIC.price}
                          hint={
                            <PriceIgvHint
                              netPrice={item.unit_price}
                              igvRate={formData.igv_rate}
                              hasIgv={item.has_igv ?? true}
                              pricesIncludeIgv={pricesIncludeIgv}
                            />
                          }
                        >
                          <div className="relative w-full">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                            <UnitPriceTextInput
                              netPrice={item.unit_price}
                              onNetPriceChange={(net) => handleItemChange(index, 'unit_price', net)}
                              pricesIncludeIgv={pricesIncludeIgv}
                              hasIgv={item.has_igv ?? true}
                              igvRate={formData.igv_rate}
                              className={`w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                                errors[`items.${index}.unit_price`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                              }`}
                              placeholder="0.0000"
                            />
                          </div>
                        </LineItemField>

                        <LineItemField label="IGV" className={POIC.igv}>
                          <select
                            value={(item.has_igv ?? true) ? 'true' : 'false'}
                            onChange={(e) => handleItemChange(index, 'has_igv', e.target.value)}
                            className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          >
                            <option value="true">Sí</option>
                            <option value="false">No</option>
                          </select>
                        </LineItemField>

                        <LineItemField
                          label="Subtotal"
                          className={POIC.subtotal}
                          hint={
                            (item.has_igv ?? true) ? (
                              <span className="text-gray-500 truncate">
                                S/ {(
                                  parseFloat(subtotal) * (1 + (parseFloat(formData.igv_rate) || 18) / 100)
                                ).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} con IGV
                              </span>
                            ) : null
                          }
                        >
                          <div className="w-full h-[34px] px-2 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-md text-gray-700 font-medium flex items-center">
                            S/ {parseFloat(subtotal).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </LineItemField>

                        <LineItemField hideLabel className={POIC.actions}>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="h-[34px] w-full flex items-center justify-center bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                            title="Eliminar item"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </LineItemField>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Totales */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-end">
                <div className="w-full md:w-80 space-y-2">
                  {(() => {
                    const calculated = calculateTotal();
                    const totals = amountOverride || calculated;
                    const base = totals.baseImponible;
                    const noGrav = totals.noGravados;
                    if (!isEditingAmounts) {
                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Base Imponible:</span>
                            <span className="font-medium text-gray-700">S/ {parseFloat(base).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">IGV ({formData.igv_rate}%):</span>
                            <span className="font-medium text-gray-700">S/ {parseFloat(totals.igv).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">No Gravados:</span>
                            <span className="font-medium text-gray-700">S/ {parseFloat(noGrav).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-300">
                            <span className="text-base font-semibold text-gray-800">Total:</span>
                            <span className="text-2xl font-bold text-teal-600">S/ {parseFloat(totals.total).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="pt-2">
                            <button type="button" onClick={() => { setAmountForm({ igv_amount: String(totals.igv) }); setIsEditingAmounts(true); }} className="text-sm text-teal-600 hover:text-teal-700 font-medium" title="Ajustar IGV por diferencias de decimales">Ajustar montos</button>
                          </div>
                        </>
                      );
                    }
                    const igvNum = parseFloat(amountForm.igv_amount) || 0;
                    const totalRecalc = Math.round((base + igvNum + noGrav) * 100) / 100;
                    return (
                      <div className="pt-2 border-t border-gray-300 space-y-3">
                        <p className="text-xs text-gray-500">Modifique el IGV para corregir la diferencia por decimales. El Total se recalcula automáticamente.</p>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">IGV</label>
                          <input type="number" step="0.01" min="0" value={amountForm.igv_amount} onChange={(e) => setAmountForm({ igv_amount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Total (recalculado)</label>
                          <p className="text-sm font-semibold text-teal-600 py-1.5">S/ {totalRecalc.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => { setAmountOverride({ baseImponible: base, igv: igvNum, noGravados: noGrav, total: totalRecalc }); setIsEditingAmounts(false); }} className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-md">Guardar</button>
                          <button type="button" onClick={() => setIsEditingAmounts(false)} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium rounded-md">Cancelar</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Sección: Observaciones */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Observaciones
            </h3>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              placeholder="Agregar notas u observaciones sobre la orden..."
            />
          </div>
        </fieldset>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={isPageMode ? () => window.history.back() : onClose}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {readOnly ? 'Volver' : 'Cancelar'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Guardando...' : (order ? 'Actualizar' : 'Crear Orden')}
              </button>
            )}
          </div>
      </form>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      <ComprasItemsImportPreviewModal
        isOpen={!!importPreview}
        onClose={() => setImportPreview(null)}
        onConfirm={handleConfirmImportPreview}
        fileName={importPreview?.fileName}
        items={importPreview?.items || []}
        errors={importPreview?.errors || []}
        warnings={importPreview?.warnings || []}
        projects={projects}
        products={products}
        units={units}
        confirmLabel="Añadir a la orden"
        entityLabel="la orden de compra"
      />

      <CreateSupplierModal
        isOpen={isCreateSupplierModalOpen}
        onClose={() => setIsCreateSupplierModalOpen(false)}
        onSuccess={handleSupplierCreated}
        forceProviderOnly={true}
      />

      <RequirementSelectorModal
        isOpen={isRequirementSelectorModalOpen}
        onClose={() => setIsRequirementSelectorModalOpen(false)}
        requirements={pendingRequirements}
        linkedRequirementIds={linkedRequirementIds}
        loading={loadingRequirements}
        onConfirm={importFromRequirements}
      />
    </>
  );

  const loadingView = (
    <FormLoadingSpinner
      message="Cargando formulario…"
      hint="Preparando proveedores, productos y datos de la orden"
      compact={!isPageMode}
    />
  );

  if (isPageMode) {
    return (
      <>
        {isInitialLoading ? loadingView : formContent}
      </>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${isOpen ? 'block' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="bg-teal-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {order ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {isInitialLoading ? loadingView : formContent}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderForm;

